import { tagged } from "@mirage/service-logging";
import { isDefined } from "@mirage/shared/util/tiny-utils";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useCallback, useEffect } from "react";
import { fetchUserAccess, fetchUserEligibility } from "..";

export type DashUserAttributes = {
  isDfbUser: boolean;
  isEligible: boolean;
  canSeeDfbInviteMemberForm: boolean;
  hasDashInFss: boolean;
  skipOnRailsOnboarding: boolean;
  hasChatInFss: boolean;
  hasDashInFssDesktop: boolean;
  hasProtectAndControl: boolean;
  hasStacksInFss: boolean;
};

export const userAttributesAtom = atomWithStorage<DashUserAttributes | null>(
  "dashUserAttributes",
  null,
);

const logger = tagged("useDashUserAttributes");

export const useDashUserAttributes = () => {
  const [userAttributes, setUserAttributes] = useAtom(userAttributesAtom);

  const fetchAttributes = useCallback(async () => {
    try {
      const [eligibility, access] = await Promise.all([
        fetchUserEligibility(),
        fetchUserAccess(),
      ]);
      const { dash_user_attributes } = eligibility || {};
      const allAttributes =
        access.category_results?.map((r) => r.attributes).filter(isDefined) ||
        [];
      const featureAttributes = allAttributes.find(
        (attr) => attr[".tag"] === "feature_attributes",
      );

      if (dash_user_attributes) {
        setUserAttributes({
          isDfbUser: dash_user_attributes.is_dfb_user || false,
          isEligible: dash_user_attributes.is_eligible || false,
          canSeeDfbInviteMemberForm:
            dash_user_attributes.can_see_dfb_invite_member_form || false,
          hasDashInFss: dash_user_attributes.has_dash_in_fss || false,
          hasChatInFss: featureAttributes?.has_chat_in_fss || false,
          hasDashInFssDesktop:
            featureAttributes?.has_dash_in_fss_desktop || false,
          hasProtectAndControl:
            featureAttributes?.has_protect_and_control || false,
          hasStacksInFss: featureAttributes?.has_stacks_in_fss || false,
          skipOnRailsOnboarding:
            dash_user_attributes.skip_on_rails_onboarding || false,
        });
      }
    } catch (err) {
      logger.error("Failed to fetch user attributes", err);
    }
  }, [setUserAttributes]);

  useEffect(() => {
    if (userAttributes === null) {
      fetchAttributes();
    }
  }, [userAttributes, fetchAttributes]);

  return {
    userAttributes,
    refetch: fetchAttributes,
  };
};
